import { useEffect, useRef } from 'react';
import { Browser } from '@capacitor/browser';
import { useQueryClient } from '@tanstack/react-query';

import { clearPendingZoomCompletionKey, completeZoomConnect, getPendingZoomCompletionKey } from '@/shared/auth/zoomConnect';
import { useToastStore } from '@/shared/store/toastStore';

/**
 * 콜백이 도착하기 전엔 계속 PENDING이라, 로그인·2FA 등으로 오래 걸려도 버틸 수 있게 넉넉히 잡았다
 * (2초 x 150회 ≈ 5분, completionKey의 서버측 만료 시간(10분)보다 충분히 짧게).
 */
const RETRY_DELAY_MS = 2000;
const MAX_RETRIES = 150;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Zoom 계정 연결을 인앱 브라우저 뒤에서 자동으로 마무리한다 (네이티브 전용 — 웹은 zoomConnect.ts의
 * watchWebZoomPopup이 openZoomConnect() 안에서 직접 처리하며, 이 훅과는 로직을 공유하지 않는다.
 * 같은 화면 문구·재시도 상수를 억지로 맞추기보다, 이미 검증된 네이티브 흐름을 그대로 두는 쪽이 안전하다).
 *
 * - browserPageLoaded(브라우저가 열려서 최초 페이지가 뜬 시점)부터 미리 /oauth/complete를 폴링 시작한다.
 *   콜백이 아직 안 왔으면 PENDING이라 계속 재시도하고, 연결이 끝나는 순간 앱이 직접 Browser.close()로
 *   브라우저를 닫는다 — 사용자가 손으로 닫을 필요가 없다.
 * - browserFinished(사용자가 폴링이 끝나기 전에 직접 닫아버린 경우)는 보완용 — 이미 끝나서
 *   completionKey가 없으면 조용히 무시한다.
 * - 두 이벤트 모두 TestGate 연결(같은 Browser 플러그인 사용)이 열리고 닫힐 때도 같이 울리지만,
 *   그때는 completionKey가 없으니(openZoomConnect를 거치지 않았으므로) 반응하지 않는다.
 *
 * 호출부에서 바로 폴링하지 않고 전역 이벤트로 잡는 이유: Zoom 로그인은 몇 분씩 걸릴 수 있어
 * 그사이 OS가 앱을 종료시킬 수 있는데, 네이티브 브라우저는 앱과 별개로 살아있어 재실행 시
 * 이 리스너가 다시 붙어 폴링을 이어받을 수 있다.
 */
export function useZoomConnectListener() {
  const queryClient = useQueryClient();
  const isRunningRef = useRef(false);

  useEffect(() => {
    const finish = async (completionKey: string) => {
      if (isRunningRef.current) return;
      isRunningRef.current = true;

      try {
        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
          const { outcome } = await completeZoomConnect(completionKey);

          if (outcome === 'connected') {
            clearPendingZoomCompletionKey();
            await Browser.close().catch(() => {});
            queryClient.invalidateQueries({ queryKey: ['home'], refetchType: 'active' });
            queryClient.invalidateQueries({ queryKey: ['studentProfile'], refetchType: 'active' });
            queryClient.invalidateQueries({ queryKey: ['zoomConnection'], refetchType: 'active' });
            useToastStore.getState().showToast('success', 'Zoom 계정이 연결됐어요.', '5rem');
            return;
          }

          if (outcome === 'failed') {
            clearPendingZoomCompletionKey();
            await Browser.close().catch(() => {});
            useToastStore.getState().showToast('error', 'Zoom 연결에 실패했어요. 다시 시도해주세요.', '5rem');
            return;
          }

          // pending — 콜백이 아직 안 왔으면 잠깐 기다렸다 재시도
          await wait(RETRY_DELAY_MS);
        }

        clearPendingZoomCompletionKey();
        useToastStore.getState().showToast('error', 'Zoom 연결 확인이 지연되고 있어요. 홈 화면에서 다시 확인해주세요.', '5rem');
      } finally {
        isRunningRef.current = false;
      }
    };

    const pageLoadedHandle = Browser.addListener('browserPageLoaded', () => {
      const completionKey = getPendingZoomCompletionKey();
      if (completionKey) finish(completionKey);
    });

    const finishedHandle = Browser.addListener('browserFinished', () => {
      const completionKey = getPendingZoomCompletionKey();
      if (completionKey) finish(completionKey);
    });

    return () => {
      pageLoadedHandle.then((h) => h.remove());
      finishedHandle.then((h) => h.remove());
    };
  }, [queryClient]);
}
